import React, { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { HanddrawnArrow } from './HanddrawnArrow'
import { ArcOrbitButton } from './ArcOrbitButton'

export default function HeroVideo() {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const [isFullscreenModal, setIsFullscreenModal] = useState(false)
  const modalVideoRef = useRef(null)

  // Autentisk video og plakat fra Emmerske Efterskole
  const bgVideoSrc = '/_assets/emmerskeefterskole-rez2_8874022f.mp4'
  const presentationVideoSrc = '/_assets/emmerske-efterskole-1080p_2242791a.mp4'
  const posterSrc = '/_assets/emmerskeefterskole-rez2-newposter_c606208b.jpg'

  // Autoplay i baggrunden ved sideindlæsning (muted for browsersupport)
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true
      const playPromise = videoRef.current.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true)
          })
          .catch((err) => {
            console.warn('Hero video autoplay was prevented or delayed:', err)
          })
      }
    }
  }, [])

  // Start fuldskærms præsentation med fuld lyd når der klikkes på play
  const handleStartPlay = () => {
    setIsFullscreenModal(true)
    setTimeout(() => {
      if (modalVideoRef.current) {
        modalVideoRef.current.currentTime = 0
        modalVideoRef.current.muted = false
        modalVideoRef.current.play().catch(() => {})
      }
    }, 50)
  }

  const closeFullscreenModal = () => {
    if (modalVideoRef.current && videoRef.current) {
      videoRef.current.currentTime = modalVideoRef.current.currentTime
      modalVideoRef.current.pause()
      if (isPlaying) {
        videoRef.current.play().catch(() => {})
      }
    }
    setIsFullscreenModal(false)
  }

  return (
    <section className="relative w-full overflow-hidden bg-[#36454F]" aria-label="Forside hero med videobaggrund">
      {/* 100vh Hero Container with Centered Composition */}
      <div
        ref={containerRef}
        className="relative w-full min-h-[640px] h-[100svh] max-h-[1050px] flex items-center justify-center overflow-hidden"
      >
        {/* Full-bleed Background Video */}
        <video
          ref={videoRef}
          src={bgVideoSrc}
          poster={posterSrc}
          autoPlay
          muted={isMuted}
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Cinematic Dark Gradient Overlay for Maximum Legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.22) 45%, rgba(0,0,0,0.45) 100%)',
          }}
          aria-hidden="true"
        />

        {/* Subtly Floating Background Blob for Organic Depth */}
        <div
          className="absolute top-1/4 -right-24 w-96 h-96 rounded-organic bg-[#007A53]/20 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-1/4 -left-24 w-96 h-96 rounded-organic bg-[#2F6F73]/25 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* All-Inclusive badge – placeret i toppen til højre, flugtende med menubarens højre side */}
        <div className="absolute top-22 sm:top-24 md:top-28 left-0 right-0 z-30 pointer-events-none">
          <div className="w-[calc(100%-24px)] md:w-[calc(100%-40px)] max-w-[1720px] mx-auto flex justify-end">
            <div className="pointer-events-auto group relative">
              <img
                src="https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/ee-allinclusive.png"
                alt="All Inclusive"
                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain drop-shadow-[0_10px_25px_rgba(0,0,0,0.45)] transform rotate-6 hover:rotate-12 hover:scale-110 transition-all duration-300 select-none cursor-default"
                loading="eager"
              />
            </div>
          </div>
        </div>

        {/* Centered Content Composition */}
        <div className="relative z-20 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 sm:pt-28 pb-12 sm:pb-16 flex flex-col items-center justify-center">
          {/* Eyebrow in Caveat Handwritten Typeface */}
          <div className="mb-2 sm:mb-3 flex flex-col items-center">
            <span className="font-['Shadows_Into_Light'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#DCEEF2] inline-block transform -rotate-2 drop-shadow-md">
              Emmerske Efterskole
            </span>
            <div className="font-['Shadows_Into_Light'] text-base sm:text-lg md:text-xl text-white/95 inline-block transform -rotate-1 drop-shadow-sm mt-1 sm:mt-2 text-left leading-snug">
              <span className="block">– For elever der er ordblinde</span>
              <span className="block">– ikke trives i skolen</span>
              <span className="block">– har det fagligt svært</span>
              <span className="block">– har højt fravær</span>
              <span className="block">– har DLD eller APD</span>
            </div>
          </div>

          {/* Heavy, Large Main Heading (Plus Jakarta Sans 800) */}
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white text-center leading-[1.03] tracking-tight max-w-5xl mx-auto drop-shadow-xl mb-5 sm:mb-6">
            Skuldrene ned,{' '}
            <span className="relative inline-block text-white">
              selvværdet op
              <svg
                className="absolute -bottom-2 sm:-bottom-3 left-0 w-full h-3 sm:h-4 text-[#007A53] pointer-events-none"
                viewBox="0 0 160 14"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M3 10C42 3 118 3 157 9"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h1>

          {/* Call to Action Buttons – Arc & Orbit med Shape Morphing */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full max-w-md sm:max-w-none">
            {/* Primary Arc & Orbit Button (Pantone 341) */}
            <ArcOrbitButton
              to="/rundvisning"
              className="w-full sm:w-auto"
            >
              Book en rundvisning
            </ArcOrbitButton>

            {/* Video Play Button (Arc & Orbit sekundær videoknap) */}
            <ArcOrbitButton
              variant="secondary"
              onClick={handleStartPlay}
              icon={
                <span className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center shadow-xs shrink-0">
                  <i className="fa-solid fa-play text-xs ml-0.5" aria-hidden="true" />
                </span>
              }
              arrow={false}
              className="w-full sm:w-auto"
              ariaLabel="Se video om Emmerske Efterskole med lyd"
            >
              Se en video om skolen
            </ArcOrbitButton>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none text-white/70 flex flex-col items-center gap-1 text-xs">
          <span className="font-['Shadows_Into_Light'] text-lg text-[#DCEEF2]">Oplev efterskolen</span>
          <i className="fa-solid fa-chevron-down animate-bounce text-sm text-[#DCEEF2]" aria-hidden="true" />
        </div>
      </div>

      {/* Nøgletal Bar i grøn (#007A53) under Hero */}
      <div className="bg-[#007A53] text-white py-8 sm:py-10 px-4 sm:px-6 relative z-10 border-b border-[#006544] shadow-inner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur-sm border border-white/15 text-center sm:text-left transition-transform hover:scale-[1.02]">
              <div className="font-['Plus_Jakarta_Sans'] text-2xl sm:text-3xl lg:text-4xl font-black text-white">Små klasser</div>
              <div className="text-xs sm:text-sm text-[#DCEEF2] font-semibold mt-1">Trygt og tæt fællesskab</div>
            </div>
            <div className="bg-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur-sm border border-white/15 text-center sm:text-left transition-transform hover:scale-[1.02]">
              <div className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-black text-white">8., 9. & 10.</div>
              <div className="text-xs sm:text-sm text-[#DCEEF2] font-semibold mt-1">Klasse med fuld afgangsprøve</div>
            </div>
            <div className="bg-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur-sm border border-white/15 text-center sm:text-left transition-transform hover:scale-[1.02]">
              <div className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-black text-white">100%</div>
              <div className="text-xs sm:text-sm text-[#DCEEF2] font-semibold mt-1">Fokus på ordblindhed, DLD, APD og LST</div>
            </div>
            <div className="bg-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur-sm border border-white/15 text-center sm:text-left transition-transform hover:scale-[1.02]">
              <div className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-black text-white">11+</div>
              <div className="text-xs sm:text-sm text-[#DCEEF2] font-semibold mt-1">Kreative & praktiske valgfag</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal til fuldskærms videovisning */}
      {isFullscreenModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-lg flex flex-col items-center justify-center p-2 sm:p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Præsentationsvideo om Emmerske Efterskole"
        >
          <div className="absolute top-3 right-3 sm:top-5 sm:right-5 z-30">
            <button
              type="button"
              onClick={closeFullscreenModal}
              className="bg-white/20 hover:bg-white text-white hover:text-[#36454F] px-4 py-2 rounded-full text-xs sm:text-sm font-bold backdrop-blur-md border border-white/40 flex items-center gap-2 shadow-xl cursor-pointer transition-colors"
            >
              <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
              <span>Luk video</span>
            </button>
          </div>

          <div className="w-full h-full max-w-[96vw] max-h-[92vh] flex items-center justify-center">
            <video
              ref={modalVideoRef}
              src={presentationVideoSrc}
              controls
              autoPlay
              playsInline
              className="w-full h-full max-h-[92vh] rounded-xl sm:rounded-2xl shadow-2xl object-contain bg-black"
            />
          </div>
        </div>
      )}
    </section>
  )
}
